import { z } from 'zod'
import { ShowPackageImportRequest } from './show-package-import.js'

const Key = z.string().min(1).max(256)
const Digest = z.string().regex(/^sha256:[a-f0-9]{64}$/)
export const InstallationDescriptor = z.strictObject({
  installationKey: Key,
  publicKey: z.string().min(1).max(4096),
  fingerprint: Digest,
})
const signed = <T extends z.ZodType>(body: T) =>
  z.strictObject({ body, signature: z.string().min(1).max(256) })
const version = z.literal('honeyshow-handover.v1')
export const HandoverRoot = signed(
  z.strictObject({
    protocol: version,
    kind: z.literal('root'),
    rootKey: z.uuid(),
    packageKey: Key,
    showKey: Key,
    owner: InstallationDescriptor,
    epoch: z.literal(0),
  })
)
export const HandoverGrant = signed(
  z.strictObject({
    protocol: version,
    kind: z.literal('grant'),
    rootKey: z.uuid(),
    packageKey: Key,
    showKey: Key,
    transferKey: z.uuid(),
    epoch: z.number().int().positive(),
    parentDigest: Digest,
    from: InstallationDescriptor,
    to: InstallationDescriptor,
    intentDigest: Digest,
    checkpointDigest: Digest.nullable(),
    packageDigest: Digest,
  })
)
export const HandoverReceipt = signed(
  z.strictObject({
    protocol: version,
    kind: z.literal('receipt'),
    outcome: z.enum(['activated', 'cancelled']),
    transferKey: z.uuid(),
    grantDigest: Digest,
    epoch: z.number().int().positive(),
    acceptedDigest: Digest.nullable(),
  })
)
export const HandoverAuthority = z.strictObject({
  root: HandoverRoot,
  steps: z.array(z.strictObject({ grant: HandoverGrant, receipt: HandoverReceipt })).max(1024),
})
export const HandoverIntent = signed(
  z.strictObject({
    protocol: version,
    kind: z.literal('intent'),
    transferKey: z.uuid(),
    packageKey: Key,
    showKey: Key,
    sourceAuthorityDigest: Digest,
    from: InstallationDescriptor,
    to: InstallationDescriptor,
    destinationShowId: Key,
    destinationRevision: z.number().int().nonnegative().nullable(),
    checkpointDigest: Digest.nullable(),
    privacyProfile: z.enum(['limited', 'full-contact']),
  })
)
export const HandoverPreparation = z.strictObject({
  intent: HandoverIntent,
  authority: HandoverAuthority,
  checkpoint: z.unknown().nullable(),
})
export const HandoverBundle = HandoverPreparation.extend({
  grant: HandoverGrant,
  package: z.unknown(),
})
export const HandoverPrepareRequest = z.strictObject({
  authority: HandoverAuthority,
  transferKey: z.uuid(),
  privacyProfile: z.enum(['limited', 'full-contact']).default('limited'),
})
export const HandoverFreezeRequest = z.strictObject({
  preparation: HandoverPreparation,
  intakeDevicesSynced: z.literal(true),
})
export const HandoverReviewRequest = z.strictObject({
  bundle: HandoverBundle,
  choices: ShowPackageImportRequest.omit({ package: true, baselines: true }).default({
    mappings: [],
    priorClasses: [],
    resolutions: [],
    clubAffiliations: [],
  }),
})
export const HandoverApplyResult = z.strictObject({
  receipt: HandoverReceipt,
  alreadyApplied: z.boolean(),
  destinationShowId: z.string(),
})
export const HandoverApplyRequest = HandoverReviewRequest.extend({ reviewToken: z.uuid() })
export const OwnershipDesignationRequest = z.strictObject({
  confirmOnlyActiveCopy: z.literal(true),
})
export const ShowOwnershipSummary = z.strictObject({
  state: z.enum(['active', 'read_only', 'outgoing', 'inactive']),
  epoch: z.number().int().nonnegative(),
  revision: z.number().int().nonnegative(),
  ownerInstallationKey: Key,
  transferKey: z.string().nullable(),
  canOperate: z.boolean(),
  canDesignate: z.boolean(),
})
export const ShowOwnershipStatus = ShowOwnershipSummary.extend({
  authority: HandoverAuthority.nullable(),
})
export type TInstallationDescriptor = z.infer<typeof InstallationDescriptor>
export type THandoverAuthority = z.infer<typeof HandoverAuthority>
export type THandoverGrant = z.infer<typeof HandoverGrant>
export type THandoverReceipt = z.infer<typeof HandoverReceipt>
export type THandoverIntent = z.infer<typeof HandoverIntent>
export type THandoverBundle = z.infer<typeof HandoverBundle>
